import React, { CSSProperties, useState } from "react";
import Slider from "@mui/material/Slider";
import {
  Typography,
  Button,
  TextField,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { bwColours } from "../theme";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import { custInfo } from "../customer";
import {
  densityToBrix,
  densityToOechsle,
  brixToDensity,
  oechsleToDensity,
} from "../density";
import { Setting, SettingInput } from "./setting";
import { Condition, ConditionInput } from "./condition";
import { ClearCheckpoint } from "./clear_checkpoint";

// Density unit configuration — ranges and labels for display conversion
const DENSITY_UNIT_CONFIG = {
  "°Bx": {
    label: "°Bx",
    fromSI: (kgm3: number) => densityToBrix(kgm3),
    toSI: (val: number) => brixToDensity(val),
    min: -2,
    max: 30,
  },
  "°Oe": {
    label: "°Oe",
    fromSI: (kgm3: number) => densityToOechsle(kgm3),
    toSI: (val: number) => oechsleToDensity(val),
    min: -10,
    max: 122,
  },
  "g/L": {
    label: "g/L",
    fromSI: (kgm3: number) => kgm3,
    toSI: (val: number) => val,
    min: 990,
    max: 1130,
  },
};

export interface Step {
  densityThreshold: number;
  densityThresholdForNextStep: number;
  settings: Setting[];
  conditions: Condition[];
  checkpoint?: {
    enabled: boolean;
    description: string;
  };
}

// Server-side data model — matches the JSON sent to/from the API
export interface ServerStep {
  densityThreshold?: number;
  densityThresholdIsRelative: boolean;
  settings?: Setting[];
  conditions?: Condition[];
  checkpoint?: { enabled: boolean; description: string };
}

type StepEditFn = (step: Step) => void;

export interface AutomationLogEntry {
  executedAt?: string;
  checkpointClearedAt?: string;
  [key: string]: unknown;
}

////////////////
// Converting from server data model to frontend data model, and back
////////////////

// Define the cutoff when the UI switches between relative and absolute density
// Uses the implicit rule: densityThreshold >= relativeDensityCutoff → relative to starting density
const relativeDensityCutoff = 1060;
const isRelativeThreshold = (
  value: number,
  cfg: typeof DENSITY_UNIT_CONFIG["°Bx"],
): boolean => value >= cfg.fromSI(relativeDensityCutoff);

// Convert server side SI density (kg/m³) with explicit "relative" flag
// to frontend data-model value (Brix/Oechsle/kg/m³) rounded to int, with implicit "relative" flag
const densityToDisplay = (
  kgm3: number,
  densityThresholdIsRelative: boolean,
  cfg: typeof DENSITY_UNIT_CONFIG["°Bx"],
): number => {
  if (densityThresholdIsRelative) {
    return Math.round(cfg.fromSI(cfg.toSI(cfg.max) - kgm3));
  }
  return Math.round(cfg.fromSI(kgm3) as number);
};

// Convert display value (rounded) back to SI density
const densityFromDisplay = (
  displayVal: number,
  cfg: typeof DENSITY_UNIT_CONFIG["°Bx"],
): { val: number; isRelative: boolean } => {
  if (isRelativeThreshold(displayVal, cfg)) {
    return {
      val: cfg.toSI(cfg.max) - cfg.toSI(displayVal),
      isRelative: true,
    };
  }
  return {
    val: cfg.toSI(displayVal),
    isRelative: false,
  };
};

// Prepare the steps data structure after reading it from the server
// Converts SI (kg/m³) → display units for the UI
// Computes densityThresholdForNextStep from the next step's threshold,
// clamped to never exceed the step's own threshold.
const stepsToUI = (
  steps: ServerStep[],
  cfg: typeof DENSITY_UNIT_CONFIG["°Bx"],
) => {
  const uiSteps = steps.map((s, i) => ({
    densityThreshold: densityToDisplay(
      s.densityThreshold ?? 0,
      s.densityThresholdIsRelative,
      cfg,
    ),
    densityThresholdForNextStep:
      i < steps.length - 1
        ? densityToDisplay(
            steps[i + 1].densityThreshold ?? 0,
            steps[i + 1].densityThresholdIsRelative,
            cfg,
          )
        : cfg.min,
    settings: s.settings || [],
    conditions: s.conditions || [],
    checkpoint: s.checkpoint
      ? {
          enabled: s.checkpoint.enabled ?? false,
          description: s.checkpoint.description ?? "",
        }
      : undefined,
  }));

  return uiSteps;
};

// Prepare the steps data structure for sending to the server
// Converts display units → SI (kg/m³)
// Uses the implicit rule: densityThreshold >= relativeDensityCutoff → relative to starting density
const stepsToAPI = (steps: Step[], cfg: typeof DENSITY_UNIT_CONFIG["°Bx"]) => {
  return steps.map(({ densityThreshold, ...rest }) => {
    const { val, isRelative } = densityFromDisplay(densityThreshold, cfg);
    return {
      densityThreshold: val,
      densityThresholdIsRelative: isRelative,
      ...rest,
    };
  });
};

// Custom styled Slider that hides the second thumb and its label
const SingleThumbSlider = styled(Slider)({
  "& .MuiSlider-thumb[data-index='1']": {
    display: "none",
  },
});

////////////////
// Controlled input component — no save/reset, no data loading
// Accepts server model (ServerStep[]) and emits server model on change
// Internal translation happens transparently via stepsToUI / stepsToAPI
////////////////

interface FermentAutomationProps {
  value: ServerStep[];
  onChange: (steps: ServerStep[]) => void;
  automationLog?: AutomationLogEntry[];
  onClearCheckpoint?: (
    stepIndex: number,
    checkpointDescription: string,
  ) => void;
  style?: CSSProperties;
  readonly?: boolean;
}

export const FermentAutomation = ({
  value: serverSteps,
  onChange: onServerChange,
  automationLog = [],
  onClearCheckpoint,
  style,
  readonly = false,
}: FermentAutomationProps) => {
  // Resolve density unit at render time so we pick up the customer preference
  const densityUnit =
    (custInfo?.customerPreferences
      ?.densityUnit as keyof typeof DENSITY_UNIT_CONFIG) ?? "°Bx";

  const DENSITY_CFG =
    DENSITY_UNIT_CONFIG[densityUnit as keyof typeof DENSITY_UNIT_CONFIG] ??
    DENSITY_UNIT_CONFIG["°Bx"];

  // Translate server model → UI model for internal display/editing
  const uiSteps = stepsToUI(serverSteps, DENSITY_CFG) as Step[];

  // Wrap onChange to translate UI model → server model before emitting
  const onUIChange = (newUiSteps: Step[]) => {
    onServerChange(stepsToAPI(newUiSteps, DENSITY_CFG));
  };

  const [stepToRemove, setStepToRemove] = useState<number | null>(null);

  // Format a date string to local time ISO format
  const formatLocalISO = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  // Find the log entry for a given step index
  const getLogForStep = (stepIndex: number) => {
    return automationLog[stepIndex] || null;
  };

  const formatValue = (value: number) => {
    if (isRelativeThreshold(value, DENSITY_CFG)) {
      const delta = DENSITY_CFG.max - value;
      return `∆${delta}${DENSITY_CFG.label}`;
    } else {
      return `${value}${DENSITY_CFG.label}`;
    }
  };

  const mkDensityThresholdChangeHandler =
    (index: number) =>
    (_event: Event, newValue: number | number[], _activeThumb: number) => {
      if (!Array.isArray(newValue)) return;

      const newSteps = [...uiSteps];
      // Limit: can't move beyond the previous step's threshold
      const stepLimit =
        index > 0 ? uiSteps[index - 1].densityThreshold : DENSITY_CFG.max;
      const newThreshold = Math.min(-newValue[0], stepLimit);

      newSteps[index] = {
        ...newSteps[index],
        densityThreshold: newThreshold,
      };

      onUIChange(newSteps);
    };

  const handleAddStep = () => {
    if (uiSteps.length === 0) {
      onUIChange([
        {
          densityThreshold: DENSITY_CFG.max,
          densityThresholdForNextStep: DENSITY_CFG.min,
          settings: [],
          conditions: [],
          checkpoint: { enabled: false, description: "" },
        },
      ]);
      return;
    }

    const newSteps = [...uiSteps];
    const lastIndex = newSteps.length - 1;
    const lastStep = newSteps[lastIndex];

    const range =
      lastStep.densityThreshold - lastStep.densityThresholdForNextStep;
    const midpoint = lastStep.densityThresholdForNextStep + range / 2;

    // Adjust the current last step to end at the midpoint
    newSteps[lastIndex] = {
      ...lastStep,
      densityThresholdForNextStep: midpoint,
    };

    // Add the new step starting at the midpoint and ending at the old densityThresholdForNextStep
    newSteps.push({
      densityThreshold: midpoint,
      densityThresholdForNextStep: lastStep.densityThresholdForNextStep,
      settings: [],
      conditions: [],
      checkpoint: { enabled: false, description: "" },
    });

    onUIChange(newSteps);
  };

  const handleAddStepBefore = (index: number) => {
    if (uiSteps.length === 0) return;
    if (index === 0) return; // Can't add before the first step

    const newSteps = [...uiSteps];
    const prevStep = newSteps[index - 1];

    const range =
      prevStep.densityThreshold - prevStep.densityThresholdForNextStep;
    const midpoint = prevStep.densityThresholdForNextStep + range / 2;

    // The previous step now ends at the midpoint
    newSteps[index - 1] = {
      ...prevStep,
      densityThresholdForNextStep: midpoint,
    };

    // Insert a new step at `index` (shifts the old step to index + 1)
    newSteps.splice(index, 0, {
      densityThreshold: midpoint,
      densityThresholdForNextStep: prevStep.densityThresholdForNextStep,
      settings: [],
      conditions: [],
      checkpoint: { enabled: false, description: "" },
    });

    onUIChange(newSteps);
  };

  const handleRemoveStep = (stepIndex: number) => {
    const newSteps = [...uiSteps];
    const removedStep = newSteps[stepIndex];

    newSteps.splice(stepIndex, 1);

    if (newSteps.length > 0) {
      if (stepIndex > 0) {
        // Extend the previous step to cover the removed step's range
        newSteps[stepIndex - 1] = {
          ...newSteps[stepIndex - 1],
          densityThresholdForNextStep: removedStep.densityThresholdForNextStep,
          checkpoint: removedStep.checkpoint,
        };
      } else {
        // Removed the first step, extend the new first step upwards
        newSteps[0] = {
          ...newSteps[0],
          densityThreshold: removedStep.densityThreshold,
        };
      }
    }

    onUIChange(newSteps);
  };

  const editStep = (stepIndex: number, editFn: StepEditFn) => {
    const newSteps = [...uiSteps];
    const step = { ...newSteps[stepIndex] };
    editFn(step);
    newSteps[stepIndex] = step;
    onUIChange(newSteps);
  };

  const handleAddSetting = (stepIndex: number) => {
    editStep(stepIndex, (step) => {
      const settings = step.settings ? [...step.settings] : [];
      settings.push({ key: "", value: undefined });
      step.settings = settings;
    });
  };

  const handleRemoveSetting = (stepIndex: number, settingIndex: number) => {
    editStep(stepIndex, (step) => {
      const settings = step.settings ? [...step.settings] : [];
      settings.splice(settingIndex, 1);
      step.settings = settings;
    });
  };

  const handleSettingChange = (
    stepIndex: number,
    settingIndex: number,
    newSetting: any,
  ) => {
    editStep(stepIndex, (step) => {
      const settings = step.settings ? [...step.settings] : [];
      settings[settingIndex] = newSetting;
      step.settings = settings;
    });
  };

  const handleAddCondition = (stepIndex: number) => {
    editStep(stepIndex, (step) => {
      const conditions = step.conditions ? [...step.conditions] : [];
      conditions.push({ field: "", comparator: ">", value: null });
      step.conditions = conditions;
    });
  };

  const handleRemoveCondition = (stepIndex: number, conditionIndex: number) => {
    editStep(stepIndex, (step) => {
      const conditions = step.conditions ? [...step.conditions] : [];
      conditions.splice(conditionIndex, 1);
      step.conditions = conditions;
    });
  };

  const handleConditionChange = (
    stepIndex: number,
    conditionIndex: number,
    newCondition: any,
  ) => {
    editStep(stepIndex, (step) => {
      const conditions = step.conditions ? [...step.conditions] : [];
      conditions[conditionIndex] = newCondition;
      step.conditions = conditions;
    });
  };

  const handleCheckpointToggle = (stepIndex: number, enabled: boolean) => {
    editStep(stepIndex, (step) => {
      step.checkpoint = {
        enabled,
        description: step.checkpoint?.description ?? "",
      };
    });
  };

  const handleCheckpointDescriptionChange = (
    stepIndex: number,
    description: string,
  ) => {
    // Restrict to uppercase alphanumeric, max 16 characters
    const sanitized = description
      .toUpperCase()
      .replace(/[^A-Z0-9 .,<>/?:;"'[\]{}\\|~`!@#$%^&*()-_=+]/g, "")
      .slice(0, 16);
    editStep(stepIndex, (step) => {
      step.checkpoint = {
        enabled: step.checkpoint?.enabled ?? false,
        description: sanitized,
      };
    });
  };

  return (
    <div style={{ marginTop: -10, ...style }}>
      {uiSteps.map((s, index) => {
        const log = getLogForStep(index);
        const stepReadonly = readonly || !!log;

        return (
          <div key={index}>
            <div
              style={{
                borderBottom: "1px solid #eee",
                marginLeft: -16,
                marginRight: -16,
                padding: 24,
                paddingTop: 0,
                backgroundImage:
                  index === 0
                    ? undefined
                    : "linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0) 100%)",
                backgroundSize: "auto 20px",
                backgroundRepeat: "no-repeat",
                backgroundColor: log ? "#f6f6f6" : undefined,
                opacity: log ? "0.5" : undefined,
              }}
            >
              <div
                style={{
                  height: 34,
                  display: "flex",
                  justifyContent: "flex-end",
                  marginRight: -24,
                  //top: 0,
                  //marginLeft: -20,
                  //marginTop: -17,
                  //marginBottom: -17,
                  opacity: 0.7,
                }}
              >
                {index > 0 && !stepReadonly ? (
                  <>
                    <IconButton
                      onClick={() => handleAddStepBefore(index)}
                      size="small"
                    >
                      <AddIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => setStepToRemove(index)}
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  </>
                ) : null}
              </div>
              <SingleThumbSlider
                scale={(x) => {
                  return -x;
                }}
                min={-DENSITY_CFG.max}
                max={-DENSITY_CFG.min}
                style={{ marginTop: index === 0 ? 0 : 32 }}
                getAriaLabel={() => "Density range"}
                value={[
                  -s.densityThreshold,
                  -s.densityThresholdForNextStep,
                ].sort((a, b) => a - b)}
                onChange={
                  stepReadonly
                    ? undefined
                    : mkDensityThresholdChangeHandler(index)
                }
                valueLabelDisplay={stepReadonly ? "off" : "on"}
                getAriaValueText={formatValue}
                valueLabelFormat={formatValue}
                disableSwap
                sx={
                  stepReadonly
                    ? {
                        pointerEvents: "none",
                      }
                    : undefined
                }
              />

              <Box
                sx={{
                  // The conditions and settings columns do not fit side
                  // by side on a phone; stack them below sm.
                  display: { xs: "block", sm: "flex" },
                  gap: 4,
                }}
              >
                {/* Left Column for Conditions */}
                <Box
                  sx={{
                    flex: { xs: "none", sm: "1 1 0%" },
                    mb: { xs: 3, sm: 0 },
                  }}
                >
                  {s.densityThreshold === DENSITY_CFG.max ? (
                    <Typography color="#999">
                      Once fermentation is initiated and these conditions are
                      met:
                    </Typography>
                  ) : isRelativeThreshold(s.densityThreshold, DENSITY_CFG) ? (
                    <Typography color="#999">
                      After reaching {DENSITY_CFG.max - s.densityThreshold}
                      {DENSITY_CFG.label} below initial, and these conditions
                      are met:
                    </Typography>
                  ) : (
                    <Typography color="#999">
                      After reaching {s.densityThreshold}
                      {DENSITY_CFG.label}, and these conditions are met:
                    </Typography>
                  )}
                  {s.conditions &&
                    s.conditions.map(
                      (condition: any, conditionIndex: number) => (
                        <ConditionInput
                          key={conditionIndex}
                          condition={condition}
                          removeable={!stepReadonly}
                          readonly={stepReadonly}
                          onChange={(newCondition) =>
                            handleConditionChange(
                              index,
                              conditionIndex,
                              newCondition,
                            )
                          }
                          onRemove={() =>
                            handleRemoveCondition(index, conditionIndex)
                          }
                        />
                      ),
                    )}
                  {stepReadonly ? null : (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddCondition(index)}
                      style={{ marginTop: "8px" }}
                    >
                      Add Condition
                    </Button>
                  )}
                </Box>

                {/* Right Column for Settings */}
                <Box sx={{ flex: { xs: "none", sm: "1 1 0%" } }}>
                  <Typography color="#999">
                    then change these settings:
                  </Typography>
                  {s.settings &&
                    s.settings.map((setting: any, settingIndex: number) => (
                      <SettingInput
                        key={settingIndex}
                        setting={setting}
                        removeable={!stepReadonly}
                        readonly={stepReadonly}
                        onChange={(newSetting) =>
                          handleSettingChange(index, settingIndex, newSetting)
                        }
                        onRemove={() =>
                          handleRemoveSetting(index, settingIndex)
                        }
                      />
                    ))}
                  {stepReadonly ? null : (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddSetting(index)}
                      style={{ marginTop: "8px" }}
                    >
                      Add Setting
                    </Button>
                  )}
                  {log ? (
                    <Box
                      style={{
                        color: bwColours.primary.red,
                        fontWeight: "bold",
                      }}
                    >
                      Applied at: {formatLocalISO(log?.executedAt)}
                    </Box>
                  ) : null}
                  {!stepReadonly || s.checkpoint?.enabled ? (
                    <Typography color="#999" style={{ marginTop: "1em" }}>
                      then wait for manual intervention checkpoint:
                    </Typography>
                  ) : null}
                  {!stepReadonly && !s.checkpoint?.enabled ? (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleCheckpointToggle(index, true)}
                      style={{ marginTop: "8px" }}
                    >
                      Add checkpoint
                    </Button>
                  ) : null}
                  {s.checkpoint?.enabled ? (
                    <>
                      <Box display="flex" alignItems="center" mb={1} gap={2}>
                        <TextField
                          size="small"
                          label="Description"
                          value={s.checkpoint?.description ?? ""}
                          onChange={(e) =>
                            handleCheckpointDescriptionChange(
                              index,
                              e.target.value,
                            )
                          }
                          inputProps={{ maxLength: 16 }}
                          placeholder="e.g. ADD NUTRIENT"
                          disabled={stepReadonly}
                        />
                        {!stepReadonly ? (
                          <IconButton
                            onClick={() => {
                              handleCheckpointToggle(index, false);
                            }}
                            size="small"
                          >
                            <RemoveCircleIcon />
                          </IconButton>
                        ) : null}
                      </Box>
                      {log?.checkpointClearedAt ? (
                        <Box
                          style={{
                            color: bwColours.primary.red,
                            fontWeight: "bold",
                          }}
                        >
                          Cleared at: {formatLocalISO(log?.checkpointClearedAt)}
                        </Box>
                      ) : null}
                    </>
                  ) : null}
                </Box>
              </Box>
            </div>
            {s.checkpoint?.enabled && log && !log.checkpointClearedAt ? (
              <Box
                style={{
                  marginLeft: -16,
                  marginRight: -16,
                  padding: "8px 12px",
                  backgroundColor: bwColours.accent.pink,
                  color: bwColours.primary.offwhite,
                  fontWeight: "bold",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Blocked on checkpoint:
                <ClearCheckpoint
                  checkpointDescription={s.checkpoint?.description ?? ""}
                  onClear={() =>
                    onClearCheckpoint?.(index, s.checkpoint?.description ?? "")
                  }
                  buttonLabel={s.checkpoint?.description ?? ""}
                  buttonStyle={{ marginLeft: 12 }}
                />
              </Box>
            ) : null}
          </div>
        );
      })}
      <Box
        style={{
          marginLeft: -16,
          marginRight: -16,
          marginBottom: 0,
          padding: 12,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button onClick={handleAddStep} disabled={readonly}>
          <AddIcon fontSize="small" style={{ marginRight: 8 }} /> Add Phase
        </Button>
      </Box>

      <Dialog
        open={stepToRemove !== null}
        onClose={() => setStepToRemove(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Remove Phase?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to remove this phase?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStepToRemove(null)}>Cancel</Button>
          <Button
            onClick={() => {
              if (stepToRemove !== null) {
                handleRemoveStep(stepToRemove);
                setStepToRemove(null);
              }
            }}
            color="error"
            autoFocus
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
